import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import { Redirect, useParams } from "react-router-dom";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/certificate.css";
import myToast from "../Utils/MyToast";
import Iframe from "react-iframe";
import MdlSelectMultipleStudent from "../dialog/MdlSelectMultipleStudent";
import BackButton from "../ChildComponent/BackButton";

const API_BASE = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;
const SERVER = process.env.REACT_APP_SERVER_NAME || "";
const SYSTEM_CODE = process.env.REACT_APP_SYSTEM_CODE || "";
const DEFAULT_DOCUMENT_TYPE = "student_certificate";
const PREVIEW_LOADER_MS = 2500;

const CERTIFICATE_TYPES = [
  {
    value: "BONAFIDE_CERTIFICATE",
    label: "Bonafide Certificate",
    title: "BONAFIDE CERTIFICATE",
    icon: "fa-id-card",
    color: "#3b82f6",
    code: "BONAFIDE",
  },
  {
    value: "CHARACTER_CERTIFICATE",
    label: "Character Certificate",
    title: "CHARACTER CERTIFICATE",
    icon: "fa-user-check",
    color: "#10b981",
    code: "CHARACTER",
  },
  {
    value: "STUDY_CERTIFICATE",
    label: "Study Certificate",
    title: "STUDY CERTIFICATE",
    icon: "fa-book",
    color: "#6366f1",
    code: "STUDY",
  },
  {
    value: "FEE_CERTIFICATE",
    label: "Fee Certificate",
    title: "FEE CERTIFICATE",
    icon: "fa-rupee",
    color: "#f97316",
    code: "FEE",
  },
  {
    value: "ATTENDANCE_CERTIFICATE",
    label: "Attendance Certificate",
    title: "ATTENDANCE CERTIFICATE",
    icon: "fa-calendar-check-o",
    color: "#a855f7",
    code: "ATTENDANCE",
  },
];

const CERT_TYPE_BY_VALUE = CERTIFICATE_TYPES.reduce((acc, t) => {
  acc[t.value] = t;
  return acc;
}, {});

const IFRAME_STYLE = {
  border: "none",
  backgroundColor: "transparent",
  overflow: "hidden",
  display: "block",
  width: "1200px",
  maxWidth: "100%",
};

const PREVIEW_OVERLAY_STYLE = {
  position: "absolute",
  inset: 0,
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.55)",
  pointerEvents: "none",
};

//Created By Cursor
function normalizeDir(dir) {
  if (!dir) return "";
  return dir.endsWith("/") ? dir : `${dir}/`;
}

//Created By Cursor
function resolveSessionId(students) {
  const fromStudent = (students || []).find((s) => s?.session_id)?.session_id;
  return fromStudent || SessionManager.getCurrentSession()?.[0]?.session_id || "";
}

//Created By Cursor
function getColorOptions(sample) {
  const raw = sample?.color_options;
  if (!Array.isArray(raw)) return [];
  return raw.filter((c) => c?.sample_color_code);
}

//Created By Cursor — pure URL builder (no React state)
function buildFileUrl({ students, sample, colorOptionId, certType }, soft = false) {
  const list = Array.isArray(students) ? students : [];
  const fileName = sample?.file_name || "";
  const dir = normalizeDir(sample?.file_directory || "");
  const sampleId = sample?.sample_id || "";
  const sessionId = resolveSessionId(list);
  const ids = list.map((s) => s.student_enrollment_no).filter(Boolean);
  const type = CERT_TYPE_BY_VALUE[certType] || CERTIFICATE_TYPES[0];

  const warn = (msg) => {
    if (!soft) myToast.notify_warning(msg);
  };

  if (!fileName) {
    warn("No certificate template file configured.");
    return "";
  }
  if (!ids.length) {
    warn("Please select at least one student.");
    return "";
  }
  if (!sessionId) {
    warn("Session is missing. Select class again.");
    return "";
  }

  const q = new URLSearchParams({
    student_ids: JSON.stringify(ids),
    class_id: list[0]?.class_id || "",
    section_id: list[0]?.section_id || "",
    session_id: sessionId,
    sample_id: sampleId,
    selection_type: "student",
    certificate_code: type.code || "BONAFIDE",
    system_code: SYSTEM_CODE,
    color_option_id: colorOptionId || "",
    certificate_title: type.title || "BONAFIDE CERTIFICATE",
    school_code: SessionManager.getSchoolCode() || "",
  });

  return `${SERVER}${dir}${fileName}?${q.toString()}`;
}

const StudentCertificate = () => {
  const { document_type: routeDocType } = useParams();
  const documentType = (routeDocType || "").trim() || DEFAULT_DOCUMENT_TYPE;

  const loggedIn = !!localStorage.getItem("token");
  const [loadingSamples, setLoadingSamples] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [samples, setSamples] = useState([]);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [colorIndex, setColorIndex] = useState(0);
  const [certType, setCertType] = useState(CERTIFICATE_TYPES[0].value);
  const [fileUrl, setFileUrl] = useState("");
  const [iframeKey, setIframeKey] = useState(0);
  const [height, setHeight] = useState("900px");

  const studentsRef = useRef(students);
  const samplesRef = useRef(samples);
  const sampleIndexRef = useRef(sampleIndex);
  const colorIndexRef = useRef(colorIndex);
  const certTypeRef = useRef(certType);

  studentsRef.current = students;
  samplesRef.current = samples;
  sampleIndexRef.current = sampleIndex;
  colorIndexRef.current = colorIndex;
  certTypeRef.current = certType;

  const activeSample = samples[sampleIndex] || null;
  const colorOptions = useMemo(() => getColorOptions(activeSample), [activeSample]);
  const studentCount = students.length;

  //Created By Cursor
  const pushPreview = (next = {}) => {
    const nextStudents = next.students ?? studentsRef.current;
    const nextSamples = next.samples ?? samplesRef.current;
    const nextSampleIdx = next.sampleIndex ?? sampleIndexRef.current;
    const nextColorIdx = next.colorIndex ?? colorIndexRef.current;
    const nextCertType = next.certType ?? certTypeRef.current;
    const sample = nextSamples[nextSampleIdx];
    const colors = getColorOptions(sample);
    const colorOptionId =
      next.colorOptionId ??
      colors[Math.min(nextColorIdx, Math.max(colors.length - 1, 0))]?.color_option_id ??
      "";

    if (!nextStudents.length) {
      setFileUrl("");
      return;
    }

    const url = buildFileUrl({
      students: nextStudents,
      sample,
      colorOptionId,
      certType: nextCertType,
    });
    if (!url) return;

    setFileUrl(url);
    setIframeKey((k) => k + 1);
    setPreviewLoading(true);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.iframeHeight) {
        setHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", onMessage, false);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  //Created By Cursor — clear overlay if iframe onLoad never fires
  useEffect(() => {
    if (!previewLoading) return undefined;
    const timer = window.setTimeout(() => setPreviewLoading(false), PREVIEW_LOADER_MS);
    return () => window.clearTimeout(timer);
  }, [previewLoading, iframeKey]);

  //Created By Cursor — load samples; abort stale requests
  useEffect(() => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setLoadingSamples(false);
      setSamples([]);
      myToast.notify_warning("School code missing.");
      return undefined;
    }

    const controller = new AbortController();
    setLoadingSamples(true);

    axios
      .get(`${API_BASE}getPrintableSample.php`, {
        params: { school_code: schoolCode, document_type: documentType },
        signal: controller.signal,
      })
      .then((res) => {
        if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
          const list = res.data.data;
          setSamples(list);
          setSampleIndex(0);
          setColorIndex(0);
          pushPreview({ samples: list, sampleIndex: 0, colorIndex: 0 });
        } else {
          setSamples([]);
          setFileUrl("");
          myToast.notify_warning(
            res.data?.api_remark || "No certificate templates found."
          );
        }
      })
      .catch((err) => {
        if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
        console.error(err);
        setSamples([]);
        setFileUrl("");
        myToast.notify_warning("Failed to load certificate templates.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingSamples(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType]);

  //Created By Cursor
  const changeSample = (index) => {
    if (index === sampleIndex) return;
    setSampleIndex(index);
    setColorIndex(0);
    pushPreview({ sampleIndex: index, colorIndex: 0 });
  };

  //Created By Cursor
  const changeColor = (index) => {
    if (index === colorIndex || !colorOptions[index]) return;
    setColorIndex(index);
    pushPreview({
      colorIndex: index,
      colorOptionId: colorOptions[index].color_option_id || "",
    });
  };

  //Created By Cursor
  const changeCertificateType = (type) => {
    if (type === certType) return;
    setCertType(type);
    pushPreview({ certType: type });
  };

  //Created By Cursor
  const handleSelectedStudent = (selected) => {
    const next = Array.isArray(selected) ? selected : [];
    setPickerOpen(false);
    setStudents(next);
    if (!next.length) {
      setFileUrl("");
      return;
    }
    pushPreview({ students: next });
  };

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div className="content event-certificate">
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}>Student Certificates</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="page-card">
        <div className="header-section">
          <div className="header-content">
            <h5>Student Certificates</h5>
            <small>
              Generate bonafide, character, study, fee and attendance
              certificates for students
            </small>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div className="header-icon">
              <i className="fa fa-file-text-o" />
            </div>
            <button
              type="button"
              className="selection-button"
              onClick={() => setPickerOpen(true)}
              style={{
                fontWeight: 700,
                background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              }}
            >
              <i className="fa fa-user-plus" />
              Select Students
            </button>
          </div>
        </div>

        <div className="selection-section" style={{ margin: "16px 20px 0" }}>
          <h5 className="section-title">
            <i className="fa fa-award" style={{ marginRight: 6 }} />
            Certificate Type
          </h5>
          <div className="certificate-type-grid">
            {CERTIFICATE_TYPES.map((type) => {
              const isActive = certType === type.value;
              return (
                <div
                  key={type.value}
                  className={`certificate-type-item${isActive ? " active" : ""}`}
                  onClick={() => changeCertificateType(type.value)}
                  style={{
                    borderColor: isActive ? type.color : undefined,
                    background: isActive ? `${type.color}08` : undefined,
                  }}
                >
                  <div
                    className="certificate-type-icon"
                    style={{ background: type.color }}
                  >
                    <i className={`fa ${type.icon}`} />
                  </div>
                  <span
                    className="certificate-type-label"
                    style={{ color: isActive ? type.color : undefined }}
                  >
                    {type.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="selection-section" style={{ margin: "16px 20px 0" }}>
          <h5 className="section-title">
            <i className="fa fa-certificate" style={{ marginRight: 6 }} />
            Certificate Templates
          </h5>
          {loadingSamples ? (
            <MyLoader />
          ) : samples.length === 0 ? (
            <p style={{ color: "#64748b", margin: "8px 0 0" }}>
              No templates found for document type <code>{documentType}</code>.
            </p>
          ) : (
            <div className="templates-grid">
              {samples.map((item, index) => (
                <div
                  key={item.sample_id || index}
                  className={`template-item${sampleIndex === index ? " active" : ""}`}
                  onClick={() => changeSample(index)}
                >
                  <i className="fa fa-file-text-o template-icon" />
                  <div className="template-name">
                    {item.sample_name || item.document_caption || "Template"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {colorOptions.length > 0 ? (
          <div className="selection-section" style={{ margin: "16px 20px 0" }}>
            <h5 className="section-title">
              <i className="fa fa-paint-brush" style={{ marginRight: 6 }} />
              Color Options
            </h5>
            <div
              className="color-options"
              style={{ maxWidth: 420, margin: "0 auto" }}
            >
              {colorOptions.map((item, index) => {
                const [c1, c2] = String(item.sample_color_code || "").split("_");
                const color1 = c1 || "#03A9F4";
                const color2 = c2 || color1;
                const isSelected = colorIndex === index;
                return (
                  <div
                    key={item.color_option_id || index}
                    className={`color-option${isSelected ? " active" : ""}`}
                    onClick={() => changeColor(index)}
                  >
                    <div className="color-display">
                      <div style={{ backgroundColor: color1 }} />
                      <div style={{ backgroundColor: color2 }} />
                    </div>
                    <div className="color-name">
                      {item.sample_color_name || `Color ${index + 1}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <h5 className="section-title">
          <i className="fa fa-eye" style={{ marginRight: 6, marginLeft: 20 }} />
          Preview
          {studentCount > 0 ? (
            <small style={{ marginLeft: 8, color: "#64748b", fontWeight: 600 }}>
              ({studentCount} student{studentCount === 1 ? "" : "s"})
            </small>
          ) : null}
        </h5>

        <div
          className="preview-section"
          style={{ margin: "10px 20px", padding: 0 }}
        >
          <div
            className="preview-frame"
            style={{ overflowX: "auto", overflowY: "hidden", position: "relative" }}
          >
            {studentCount === 0 ? (
              <div className="no-students-message">
                <i className="fa fa-user-plus" />
                <h4>No Students Selected</h4>
                <p>
                  Please select students or a class to preview the certificate
                </p>
              </div>
            ) : (
              <>
                {previewLoading ? (
                  <div style={PREVIEW_OVERLAY_STYLE}>
                    <MyLoader />
                  </div>
                ) : null}
                {fileUrl ? (
                  <Iframe
                    key={iframeKey}
                    url={fileUrl}
                    width="100%"
                    height={height}
                    id="iFrameStudentCertificate"
                    className="bg_card_primary_outline"
                    display="block"
                    position="relative"
                    frameBorder="0"
                    scrolling="yes"
                    style={IFRAME_STYLE}
                    onLoad={() => setPreviewLoading(false)}
                  />
                ) : (
                  <div className="no-students-message">
                    <h4>Preview unavailable</h4>
                    <p>Template or session details are missing.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {pickerOpen ? (
        <MdlSelectMultipleStudent
          actionClose={() => setPickerOpen(false)}
          actionHandleSelected={handleSelectedStudent}
        />
      ) : null}
    </div>
  );
};

export default StudentCertificate;
